import { Link } from 'react-router-dom';
import {
  CheckCircle2, AlertTriangle, Clock, Home, ArrowRight, CalendarClock, Megaphone,
  FileSignature,
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { money, date, relative, humanise } from '@/lib/format';
import { Badge, Button, Card, CardHeader, EmptyState, statusTone } from '@/components/ui';
import { usePortal } from './PortalShell';

export default function PortalHome() {
  const { data } = usePortal();
  const { currency } = useAuth();

  const owing = data.outstanding_cents;
  const pending = data.pending_cents;
  const overdue = data.overdue_cents;

  // The single most important thing on this screen: do I owe anything?
  const state = overdue > 0 ? 'overdue' : owing > 0 ? 'due' : pending > 0 ? 'pending' : 'clear';

  const HERO = {
    overdue: {
      icon: AlertTriangle,
      tone: 'text-danger',
      ring: 'border-danger/30 bg-danger/8',
      title: 'Rent is overdue',
      body: `${money(overdue, currency)} is past its due date.`,
    },
    due: {
      icon: Clock,
      tone: 'text-warn',
      ring: 'border-warn/30 bg-warn/8',
      title: 'Rent due',
      body: data.next_due_date
        ? `Due ${date(data.next_due_date)} — ${relative(data.next_due_date)}.`
        : 'You have an outstanding balance.',
    },
    pending: {
      icon: Clock,
      tone: 'text-info',
      ring: 'border-info/30 bg-info/8',
      title: 'Payment being checked',
      body: `${money(pending, currency)} is with your landlord for verification.`,
    },
    clear: {
      icon: CheckCircle2,
      tone: 'text-ok',
      ring: 'border-ok/30 bg-ok/8',
      title: "You're all paid up",
      body: data.next_due_date
        ? `Next payment due ${date(data.next_due_date)}.`
        : 'Nothing outstanding.',
    },
  }[state];

  const openCharges = data.charges.filter((c) => (c.balance_cents ?? 0) > 0);
  const recentPayments = data.payments.slice(0, 3);
  const pinned = data.announcements.filter((a) => a.is_pinned).slice(0, 2);

  return (
    <div className="space-y-6">
      {/* Balance hero */}
      <div className={`rounded-3xl border p-6 sm:p-8 ${HERO.ring}`}>
        <div className="flex items-start gap-4">
          <HERO.icon className={`h-7 w-7 shrink-0 ${HERO.tone}`} />
          <div className="min-w-0 flex-1">
            <p className="label">Your balance</p>
            <p className={`text-display mt-1 ${owing > 0 ? HERO.tone : ''}`}>
              {money(owing, currency)}
            </p>
            <p className="text-lg font-semibold mt-3">{HERO.title}</p>
            <p className="text-muted mt-1">{HERO.body}</p>

            {pending > 0 && state !== 'pending' && (
              <p className="text-sm text-info mt-2">
                {money(pending, currency)} you submitted is still being checked.
              </p>
            )}

            {owing > 0 && (
              <Link to="/portal/payments" className="inline-block mt-5">
                <Button variant="primary" icon={<ArrowRight className="h-4 w-4" />}>
                  Tell us you've paid
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/*
        One thing waiting on them, said once and at the top. A tenancy
        agreement they have not signed is the only item in this portal that
        somebody else is actively waiting for, so it earns the position.
      */}
      {data.agreement_pending && (
        <Link
          to="/portal/agreement"
          className="flex items-start gap-3 rounded-2xl border border-warn/30 bg-warn/10
                     px-4 py-3.5 hover:bg-warn/15 transition-colors"
        >
          <FileSignature className="h-5 w-5 shrink-0 text-warn mt-0.5" />
          <div className="min-w-0">
            <p className="text-sm font-semibold">Your tenancy agreement is waiting for you</p>
            <p className="text-sm text-muted mt-0.5">
              Read it and sign by typing your name and today’s date.
            </p>
          </div>
          <ArrowRight className="h-4 w-4 text-faint ml-auto shrink-0 mt-1" />
        </Link>
      )}

      {/* Where you live */}
      {data.lease && (
        <Card>
          <CardHeader title="Your home" action={
            <Link to="/portal/property"><Button size="sm">View details</Button></Link>
          } />
          <div className="p-5 sm:p-6 pt-4 grid sm:grid-cols-2 gap-x-6 gap-y-3">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-raised border border-line flex items-center
                              justify-center shrink-0 text-muted">
                <Home className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="font-semibold truncate">
                  {data.room?.name ?? data.property?.name}
                </p>
                <p className="text-xs text-faint truncate">
                  {data.room ? data.property?.name : 'Whole property'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-raised border border-line flex items-center
                              justify-center shrink-0 text-muted">
                <CalendarClock className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="font-semibold tnum">
                  {money(data.lease.rent_cents, currency)}
                  <span className="text-xs text-faint font-normal"> / {humanise(data.lease.cycle)}</span>
                </p>
                <p className="text-xs text-faint">
                  Due day {data.lease.due_day}
                  {data.lease.end_date ? ` · ends ${date(data.lease.end_date)}` : ''}
                </p>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Outstanding charges */}
      {openCharges.length > 0 && (
        <Card>
          <CardHeader title="What you owe" subtitle="Each item and when it is due" />
          <div className="p-5 sm:p-6 pt-4 space-y-2">
            {openCharges.map((c) => {
              const due = c.effective_due_date ?? c.due_date;
              const isLate = new Date(due) < new Date();
              return (
                <div key={c.id} className="flex items-center gap-3 p-3 rounded-xl border border-line bg-raised/40">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">{c.description}</p>
                    <p className="text-xs text-faint">
                      Due {date(due)}
                      {c.extended_to && <span className="text-info"> · extension granted</span>}
                      {(c.pending_cents ?? 0) > 0 && <span className="text-info"> · payment being checked</span>}
                    </p>
                  </div>
                  <span className={`font-bold tnum shrink-0 ${isLate ? 'text-danger' : ''}`}>
                    {money(c.balance_cents ?? 0, currency)}
                  </span>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Recent activity */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader
            title="Recent payments"
            action={<Link to="/portal/payments" className="text-sm link">See all</Link>}
          />
          <div className="p-5 sm:p-6 pt-4">
            {recentPayments.length === 0 ? (
              <EmptyState title="No payments yet" message="Your payment history will appear here." />
            ) : (
              <div className="space-y-2">
                {recentPayments.map((p) => (
                  <div key={p.id} className="flex items-center gap-3 py-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium tnum">{money(p.amount_cents, currency)}</p>
                      <p className="text-xs text-faint">{date(p.paid_at)} · {humanise(p.method)}</p>
                    </div>
                    <Badge tone={statusTone(p.status)} dot>{humanise(p.status)}</Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>

        <Card>
          <CardHeader
            title="Notices"
            action={<Link to="/portal/updates" className="text-sm link">See all</Link>}
          />
          <div className="p-5 sm:p-6 pt-4">
            {data.announcements.length === 0 ? (
              <EmptyState title="Nothing new" message="Notices from your landlord appear here." />
            ) : (
              <div className="space-y-3">
                {(pinned.length > 0 ? pinned : data.announcements.slice(0, 2)).map((a) => (
                  <div key={a.id} className="flex items-start gap-2.5">
                    <Megaphone className="h-4 w-4 text-faint shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{a.title}</p>
                      <p className="text-xs text-muted mt-0.5 line-clamp-2 leading-relaxed">{a.body}</p>
                      <p className="text-xs text-faint mt-1">{relative(a.published_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
